import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Chip, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  List,
  ListItem,
  ListItemText} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

// Worker role color mapping
const roleColors = {
  site_manager: '#2196f3',
  tukang: '#ff9800',
  helper: '#4caf50',
  operator: '#9c27b0',
  other: '#9e9e9e'
};

// Worker role labels
const roleLabels = {
  site_manager: 'Site Manager',
  tukang: 'Tukang',
  helper: 'Helper',
  operator: 'Operator',
  other: 'Lainnya'
};

const Teams = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentWorker, setCurrentWorker] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [filterRole, setFilterRole] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  
  // Form state
  const [formData, setFormData] = useState({
    projectId: '',
    name: '',
    role: 'tukang',
    phone: '',
    address: '',
    dailyRate: '',
    startDate: new Date(),
    endDate: null,
    isActive: true
  });

  // Fetch workers and projects on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch workers
        const workersResponse = await axios.get('/api/workforce', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Fetch projects
        const projectsResponse = await axios.get('/api/projects', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setWorkers(workersResponse.data || []);
        setProjects(projectsResponse.data.projects || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar('Gagal memuat data tim', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token, enqueueSnackbar]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    // Filter workers based on tab
    switch (newValue) {
      case 0: // All workers
        setFilterRole('');
        break;
      case 1: // Site Managers
        setFilterRole('site_manager');
        break;
      case 2: // Tukang
        setFilterRole('tukang');
        break;
      case 3: // Helper
        setFilterRole('helper');
        break;
      case 4: // Operator
        setFilterRole('operator');
        break;
      default:
        setFilterRole('');
    }
  };

  // Handle dialog open for create
  const handleCreateWorker = () => {
    setDialogMode('create');
    setFormData({
      projectId: '',
      name: '',
      role: 'tukang',
      phone: '',
      address: '',
      dailyRate: '',
      startDate: new Date(),
      endDate: null,
      isActive: true
    });
    setOpenDialog(true);
  };

  // Handle dialog open for edit
  const handleEditWorker = (worker) => {
    setDialogMode('edit');
    setCurrentWorker(worker);
    setFormData({
      projectId: worker.projectId,
      name: worker.name,
      role: worker.role,
      phone: worker.phone || '',
      address: worker.address || '',
      dailyRate: worker.dailyRate || '',
      startDate: worker.startDate ? new Date(worker.startDate) : new Date(),
      endDate: worker.endDate ? new Date(worker.endDate) : null,
      isActive: worker.isActive
    });
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentWorker(null);
  };

  // Handle form field change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handle date change
  const handleStartDateChange = (date) => {
    setFormData((prevData) => ({
      ...prevData,
      startDate: date
    }));
  };

  const handleEndDateChange = (date) => {
    setFormData((prevData) => ({
      ...prevData,
      endDate: date
    }));
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      if (dialogMode === 'create') {
        // Create worker
        const response = await axios.post('/api/workforce', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setWorkers((prevWorkers) => [...prevWorkers, response.data]);
        enqueueSnackbar('Anggota tim berhasil ditambahkan', { variant: 'success' });
      } else {
        // Update worker
        const response = await axios.put(`/api/workforce/${currentWorker.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setWorkers((prevWorkers) => 
          prevWorkers.map((worker) => (worker.id === currentWorker.id ? response.data : worker))
        );
        enqueueSnackbar('Anggota tim berhasil diperbarui', { variant: 'success' });
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error submitting worker:', error);
      enqueueSnackbar('Gagal menyimpan anggota tim', { variant: 'error' });
    }
  };

  // Handle worker deletion
  const handleDeleteWorker = async (workerId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus anggota tim ini?')) {
      try {
        await axios.delete(`/api/workforce/${workerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setWorkers((prevWorkers) => prevWorkers.filter((worker) => worker.id !== workerId));
        enqueueSnackbar('Anggota tim berhasil dihapus', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting worker:', error);
        enqueueSnackbar('Gagal menghapus anggota tim', { variant: 'error' });
      }
    }
  };

  // Filter workers based on role, project, and search query
  const filteredWorkers = workers.filter((worker) => {
    // Filter by role
    if (filterRole && worker.role !== filterRole) {
      return false;
    }
    
    // Filter by project
    if (filterProject && worker.projectId !== parseInt(filterProject)) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        worker.name.toLowerCase().includes(query) ||
        (worker.phone && worker.phone.toLowerCase().includes(query)) ||
        (worker.address && worker.address.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  // Group workers by project for grid view
  const workersByProject = filteredWorkers.reduce((acc, worker) => {
    const projectId = worker.projectId;
    if (!acc[projectId]) {
      acc[projectId] = [];
    }
    acc[projectId].push(worker);
    return acc;
  }, {});

  // DataGrid columns for table view
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Nama', flex: 1 },
    {
      field: 'role',
      headerName: 'Peran',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={roleLabels[params.value]}
          style={{
            backgroundColor: roleColors[params.value],
            color: '#fff'
          }}
          size="small"
        />
      )
    },
    { field: 'phone', headerName: 'Telepon', width: 150 },
    {
      field: 'project',
      headerName: 'Proyek',
      width: 200,
      valueGetter: (params) => {
        const project = projects.find(p => p.id === params.row.projectId);
        return project ? project.name : '-';
      }
    },
    {
      field: 'dailyRate',
      headerName: 'Upah Harian',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return 'Rp 0';
        return `Rp ${parseInt(params.value).toLocaleString('id-ID')}`;
      }
    },
    {
      field: 'startDate',
      headerName: 'Tanggal Mulai',
      width: 150,
      valueFormatter: (params) => {
        if (!params.value) return '-';
        return new Date(params.value).toLocaleDateString('id-ID');
      }
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Aktif' : 'Tidak Aktif'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Aksi',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditWorker(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Hapus">
            <IconButton
              size="small"
              onClick={() => handleDeleteWorker(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Manajemen Tim
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Kelola tim proyek konstruksi prefabrikasi, termasuk site manager, tukang, helper, dan operator.
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label="Cari Anggota Tim"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Proyek</InputLabel>
              <Select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                label="Proyek"
              >
                <MenuItem value="">Semua Proyek</MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
              >
                {viewMode === 'grid' ? 'Tampilan Tabel' : 'Tampilan Grid'}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={() => {
                  setFilterRole('');
                  setFilterProject('');
                  setSearchQuery('');
                  setTabValue(0);
                }}
              >
                Reset Filter
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={handleCreateWorker}
              >
                Tambah Anggota
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Semua" />
          <Tab label="Site Manager" />
          <Tab label="Tukang" />
          <Tab label="Helper" />
          <Tab label="Operator" />
        </Tabs>
      </Paper>
      
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      ) : viewMode === 'grid' ? (
        // Grid view grouped by project
        <Box>
          {Object.keys(workersByProject).map((projectId) => {
            const project = projects.find(p => p.id === parseInt(projectId)) || { name: 'Tidak Ada Proyek' };
            return (
              <Paper sx={{ mb: 3, overflow: 'hidden' }} key={projectId}>
                <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                  <Typography variant="h6">
                    <BusinessIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                    {project.name}
                  </Typography>
                </Box>
                <Grid container spacing={2} sx={{ p: 2 }}>
                  {workersByProject[projectId].map((worker) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={worker.id}>
                      <Card sx={{ height: '100%' }}>
                        <CardHeader
                          avatar={
                            <Avatar sx={{ bgcolor: roleColors[worker.role] }}>
                              {worker.name.charAt(0)}
                            </Avatar>
                          }
                          title={worker.name}
                          subheader={
                            <Chip
                              label={roleLabels[worker.role]}
                              size="small"
                              style={{
                                backgroundColor: roleColors[worker.role],
                                color: '#fff',
                                marginTop: 4
                              }}
                            />
                          }
                        />
                        <CardContent>
                          <List dense>
                            {worker.phone && (
                              <ListItem>
                                <ListItemText 
                                  primary="Telepon" 
                                  secondary={worker.phone} 
                                  primaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            )}
                            <ListItem>
                              <ListItemText 
                                primary="Upah Harian" 
                                secondary={worker.dailyRate ? `Rp ${parseInt(worker.dailyRate).toLocaleString('id-ID')}` : 'Rp 0'} 
                                primaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Tanggal Mulai" 
                                secondary={worker.startDate ? new Date(worker.startDate).toLocaleDateString('id-ID') : '-'} 
                                primaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText 
                                primary="Status" 
                                secondary={
                                  <Chip
                                    label={worker.isActive ? 'Aktif' : 'Tidak Aktif'}
                                    color={worker.isActive ? 'success' : 'default'}
                                    size="small"
                                  />
                                } 
                                primaryTypographyProps={{ variant: 'caption' }}
                              />
                            </ListItem>
                          </List>
                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                              size="small" 
                              startIcon={<EditIcon />}
                              onClick={() => handleEditWorker(worker)}
                            >
                              Edit
                            </Button>
                            <Button 
                              size="small" 
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDeleteWorker(worker.id)}
                            >
                              Hapus
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            );
          })}
        </Box>
      ) : (
        // Table view
        <Paper sx={{ width: '100%', height: 500 }}>
          <DataGrid
            rows={filteredWorkers}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            density="standard"
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Paper>
      )}
      
      {/* Create/Edit Worker Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Tambah Anggota Tim Baru' : 'Edit Anggota Tim'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nama"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Peran</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleFormChange}
                  label="Peran"
                >
                  <MenuItem value="site_manager">Site Manager</MenuItem>
                  <MenuItem value="tukang">Tukang</MenuItem>
                  <MenuItem value="helper">Helper</MenuItem>
                  <MenuItem value="operator">Operator</MenuItem>
                  <MenuItem value="other">Lainnya</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telepon"
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Upah Harian"
                name="dailyRate"
                type="number"
                value={formData.dailyRate}
                onChange={handleFormChange}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Proyek</InputLabel>
                <Select
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleFormChange}
                  label="Proyek"
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Tanggal Mulai"
                  value={formData.startDate}
                  onChange={handleStartDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Tanggal Selesai"
                  value={formData.endDate}
                  onChange={handleEndDateChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Alamat"
                name="address"
                value={formData.address}
                onChange={handleFormChange}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleFormChange}
                  label="Status"
                >
                  <MenuItem value={true}>Aktif</MenuItem>
                  <MenuItem value={false}>Tidak Aktif</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Batal</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {dialogMode === 'create' ? 'Tambah' : 'Simpan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Teams;
